import React, { useState, useEffect } from 'react';
import { subscriptionApi } from '../../api/subscriptionApi';
import { useNavigate } from 'react-router-dom';
import { PATHS } from '../../routes/path';
import { CheckIcon, XMarkIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import Header from '../../components/landing/Header';
import Footer from '../../components/landing/Footer';
import { useAuth } from '../../context/AuthContext';

const featureLabels = {
  mailbox: 'Mailbox & Email',
  aiAssist: 'AI Assist (Gemini)',
  whatsapp: 'WhatsApp Reminders',
  exportReports: 'Export PDF/Excel',
  gstReports: 'GST Reports',
  invoiceCustomization: 'Invoice Customization',
  scheduledEmails: 'Scheduled Emails',
  customBranding: 'Custom Branding',
  apiAccess: 'API Access',
  multiBranch: 'Multi-branch'
};

const limitLabels = {
  maxCustomers: 'Customers',
  maxTransactions: 'Transactions/mo',
  maxProducts: 'Products',
  maxBusinesses: 'Businesses',
  maxStaff: 'Staff Accounts',
  maxAiPerDay: 'AI Calls/day',
  maxScheduledEmails: 'Scheduled Emails/day'
};

const formatLimit = (val) => {
  if (val === -1) return 'Unlimited';
  if (val === 0) return 'None';
  return val;
};

const PlanDetails = () => {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchPlans = async () => {
    try {
      const res = await subscriptionApi.getPlans();
      if (res.data.success) {
        setPlans(res.data.data);
      }
    } catch (error) {
      console.error('Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchPlans();
  }, []);

  if (loading) return null;

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      <Header />
      
      <main className="pt-32 pb-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-500 hover:text-gray-900 mb-8 font-semibold transition-colors"
          >
            <ArrowLeftIcon className="w-4 h-4" /> Back to Home
          </button>

          <div className="mb-12">
            <h1 className="text-4xl font-extrabold tracking-tight text-gray-900 sm:text-5xl mb-4">
              Detailed Plan Comparison
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl">
              Compare all features and limits side-by-side to find the perfect fit for your business.
            </p>
          </div>

          {/* Billing Toggle */}
          <div className="flex mb-12">
            <div className="bg-white p-1.5 rounded-full inline-flex relative border border-gray-200 shadow-sm">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`relative px-6 py-2.5 rounded-full text-sm font-bold transition-all duration-300 z-10 ${
                  billingCycle === 'monthly'
                    ? 'text-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {billingCycle === 'monthly' && (
                  <div className="absolute inset-0 bg-[#dc343b] rounded-full -z-10 shadow-lg shadow-red-500/20"></div>
                )}
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle('yearly')}
                className={`relative px-6 py-2.5 rounded-full text-sm font-bold transition-all duration-300 z-10 ${
                  billingCycle === 'yearly'
                    ? 'text-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {billingCycle === 'yearly' && (
                  <div className="absolute inset-0 bg-[#dc343b] rounded-full -z-10 shadow-lg shadow-red-500/20"></div>
                )}
                Yearly
                <span className="absolute -top-2 -right-3 bg-green-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full shadow-sm">
                  -20%
                </span>
              </button>
            </div>
          </div>

          {/* Detailed Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {plans.map((plan) => {
              const isBadged = plan.badge?.text;
              const price = billingCycle === 'yearly' ? plan.price.yearly : plan.price.monthly;
              const monthlyEquivalent = billingCycle === 'yearly' ? Math.round(plan.price.yearly / 12) : plan.price.monthly;

              return (
                <div
                  key={plan._id}
                  className={`relative flex flex-col rounded-2xl border-2 bg-white transition-all hover:shadow-xl ${
                    isBadged ? 'border-[#dc343b] shadow-xl shadow-red-500/10' : 'border-gray-200'
                  }`}
                >
                  {isBadged && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider text-white whitespace-nowrap bg-[#dc343b]">
                      {plan.badge.text}
                    </div>
                  )}

                  <div className="p-6 flex-1 flex flex-col">
                    <h3 className="text-[20px] font-extrabold text-gray-900 mb-1">{plan.name}</h3>
                    <p className="text-[13px] text-gray-500 mb-6 leading-relaxed">{plan.description}</p>

                    <div className="mb-8">
                      {price === 0 ? (
                        <div className="flex items-baseline gap-1">
                          <span className="text-4xl font-extrabold text-gray-900">Free</span>
                          <span className="text-[13px] text-gray-500">forever</span>
                        </div>
                      ) : (
                        <div className="flex items-baseline gap-1">
                          <span className="text-[15px] font-bold text-gray-500">{plan.price.currency}</span>
                          <span className="text-4xl font-extrabold text-gray-900">{monthlyEquivalent}</span>
                          <span className="text-[13px] text-gray-500">/mo</span>
                        </div>
                      )}
                      {billingCycle === 'yearly' && price > 0 && (
                        <p className="text-[12px] text-green-600 font-semibold mt-2">
                          Billed {plan.price.currency}{price.toLocaleString('en-IN')} per year
                        </p>
                      )}
                    </div>

                    <div className="bg-gray-50 -mx-6 px-6 py-4 mb-6 border-y border-gray-100">
                      <h4 className="text-[11px] font-bold tracking-widest text-gray-400 uppercase mb-4">Usage Limits</h4>
                      <div className="space-y-3">
                        {Object.entries(plan.limits || {}).map(([key, val]) => (
                          <div key={key} className="flex items-center justify-between text-[13px]">
                            <span className="text-gray-600">{limitLabels[key] || key}</span>
                            <span className={`font-bold ${val === -1 ? 'text-green-600' : val === 0 ? 'text-gray-400' : 'text-gray-900'}`}>
                              {formatLimit(val)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="flex-1">
                      <h4 className="text-[11px] font-bold tracking-widest text-gray-400 uppercase mb-4">Included Features</h4>
                      <div className="space-y-3">
                        {Object.entries(plan.features || {}).map(([key, val]) => {
                          if (key === 'autoBackup' || key === 'prioritySupport') return null;
                          return (
                            <div key={key} className="flex items-center gap-3 text-[13px]">
                              {val ? (
                                <div className="bg-green-100 rounded-full p-0.5">
                                  <CheckIcon className="h-3.5 w-3.5 text-green-600 flex-shrink-0" strokeWidth={3} />
                                </div>
                              ) : (
                                <XMarkIcon className="h-4 w-4 text-gray-300 flex-shrink-0" />
                              )}
                              <span className={val ? 'text-gray-700 font-medium' : 'text-gray-400 line-through'}>
                                {featureLabels[key] || key}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div className="mt-8">
                      <button
                        onClick={() => user ? navigate('/pricing') : navigate(PATHS.REGISTER)}
                        className={`w-full py-3.5 rounded-xl text-[14px] font-bold transition-all duration-200 ${
                          plan.slug === 'premium'
                            ? 'bg-gradient-to-r from-[#dc343b] to-[#b3242a] hover:from-[#e34c52] hover:to-[#dc343b] text-white shadow-lg shadow-red-500/20'
                            : plan.slug === 'enterprise'
                              ? 'bg-gray-900 hover:bg-gray-800 text-white shadow-md'
                              : 'bg-gray-100 text-gray-900 hover:bg-gray-200 shadow-sm border border-gray-200'
                        }`}
                      >
                        Get Started
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default PlanDetails;
